import { useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FormModal } from "@/components/Settings/FormModal";
import { videosApi } from "@/apis";
import type { VideoItem } from "@/apis";

interface VideoFormState {
  title: string;
  description: string;
  source: "url" | "upload";
  videoUrl: string;
  videoKey: string;
  thumbnail: string;
  isActive: boolean;
}

const EMPTY_FORM: VideoFormState = {
  title: "",
  description: "",
  source: "upload",
  videoUrl: "",
  videoKey: "",
  thumbnail: "",
  isActive: true,
};

const validateHttpUrl = (value: string): string | null => {
  if (!value) return "Video URL is required.";
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return "Video URL should start with http:// or https://";
    }
    return null;
  } catch {
    return "Video URL should start with http:// or https://";
  }
};

export function VideosManager() {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [modalError, setModalError] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editing, setEditing] = useState<VideoItem | null>(null);
  const [form, setForm] = useState<VideoFormState>(EMPTY_FORM);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [existingThumbnailUrl, setExistingThumbnailUrl] = useState("");

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<VideoItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const modalTitle = useMemo(() => (editing ? "Edit Video" : "Add Video"), [editing]);

  const loadVideos = async () => {
    setLoading(true);
    setPageError("");
    try {
      const data = await videosApi.listAll();
      setVideos(data);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : "Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadVideos();
  }, []);

  useEffect(() => {
    if (!thumbnailFile) {
      setThumbnailPreview("");
      return;
    }
    const previewUrl = URL.createObjectURL(thumbnailFile);
    setThumbnailPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [thumbnailFile]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setVideoFile(null);
    setThumbnailFile(null);
    setExistingThumbnailUrl("");
    setModalError("");
    setIsModalOpen(true);
  };

  const openEdit = (video: VideoItem) => {
    const source = video.source === "upload" ? "upload" : "url";
    setEditing(video);
    setForm({
      title: video.title || "",
      description: video.description || "",
      source,
      videoUrl: source === "url" ? video.videoUrl || "" : "",
      videoKey: video.videoKey || "",
      thumbnail: video.thumbnailKey || "",
      isActive: video.isActive ?? true,
    });
    setVideoFile(null);
    setThumbnailFile(null);
    setExistingThumbnailUrl(video.thumbnail || "");
    setModalError("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setModalError("");
    setIsModalOpen(false);
  };

  const uploadThumbnailIfNeeded = async (): Promise<string> => {
    if (!thumbnailFile) return form.thumbnail;
    const upload = await videosApi.uploadThumbnail(thumbnailFile);
    return upload.thumbnail_key;
  };

  const saveVideo = async () => {
    const title = form.title.trim();
    const description = form.description.trim();
    const videoUrl = form.videoUrl.trim();

    if (!title) {
      setModalError("Title is required.");
      return;
    }
    if (form.source === "url") {
      const urlError = validateHttpUrl(videoUrl);
      if (urlError) {
        setModalError(urlError);
        return;
      }
    } else if (!videoFile && !form.videoKey) {
      setModalError("Upload a video file.");
      return;
    }

    setIsSaving(true);
    setModalError("");
    try {
      let videoKey = form.source === "upload" ? form.videoKey : "";
      if (form.source === "upload" && videoFile) {
        const upload = await videosApi.uploadVideo(videoFile);
        videoKey = upload.video_key;
      }
      const thumbnail = await uploadThumbnailIfNeeded();
      const payload = {
        title,
        description,
        source: form.source,
        videoUrl: form.source === "url" ? videoUrl : "",
        videoKey,
        thumbnail,
        isActive: form.isActive,
      };
      if (editing) {
        await videosApi.update(editing.id, payload);
      } else {
        await videosApi.create(payload);
      }
      setIsModalOpen(false);
      setVideoFile(null);
      setThumbnailFile(null);
      setExistingThumbnailUrl("");
      await loadVideos();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Failed to save video");
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteConfirm = (video: VideoItem) => {
    setVideoToDelete(video);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setDeleteConfirmOpen(false);
    setVideoToDelete(null);
  };

  const confirmDelete = async () => {
    if (!videoToDelete) return;
    setIsDeleting(true);
    setPageError("");
    try {
      await videosApi.remove(videoToDelete.id);
      setDeleteConfirmOpen(false);
      setVideoToDelete(null);
      await loadVideos();
    } catch (err) {
      setPageError(err instanceof Error ? err.message : "Failed to delete video");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl">Videos</CardTitle>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Video
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {pageError ? (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {pageError}
          </div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          </div>
        ) : null}

        {!loading && videos.length === 0 ? (
          <p className="text-sm text-muted-foreground">No videos found.</p>
        ) : null}

        {!loading && videos.length > 0 ? (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Thumbnail</th>
                  <th className="px-3 py-2 text-left font-medium">Title</th>
                  <th className="px-3 py-2 text-left font-medium">Source</th>
                  <th className="px-3 py-2 text-left font-medium">Video</th>
                  <th className="px-3 py-2 text-left font-medium">Active</th>
                  <th className="px-3 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {videos.map((video) => (
                  <tr key={video.id} className="border-t">
                    <td className="px-3 py-2">
                      {video.thumbnail ? (
                        <img
                          src={video.thumbnail}
                          alt=""
                          className="h-10 w-16 rounded object-cover"
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-3 py-2">{video.title}</td>
                    <td className="px-3 py-2">{video.source === "upload" ? "Upload" : "URL"}</td>
                    <td className="max-w-xs truncate px-3 py-2">
                      {video.source === "upload" ? video.videoKey || "Uploaded file" : video.videoUrl}
                    </td>
                    <td className="px-3 py-2">{video.isActive ? "Yes" : "No"}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(video)}>
                          <Pencil className="mr-2 h-3.5 w-3.5" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDeleteConfirm(video)}
                          disabled={isDeleting}
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardContent>

      <FormModal
        title={modalTitle}
        description="Upload a video file to S3, or paste a video URL."
        isOpen={isModalOpen}
        isSaving={isSaving}
        onClose={closeModal}
        onSubmit={saveVideo}
      >
        {modalError ? (
          <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {modalError}
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Title *</label>
            <Input
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Product walkthrough"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Video *</label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={form.source === "upload" ? "default" : "outline"}
                onClick={() => setForm((prev) => ({ ...prev, source: "upload" }))}
              >
                Upload file
              </Button>
              <Button
                type="button"
                variant={form.source === "url" ? "default" : "outline"}
                onClick={() => setForm((prev) => ({ ...prev, source: "url" }))}
              >
                Video URL
              </Button>
            </div>
            {form.source === "upload" ? (
              <div className="space-y-1">
                <Input
                  type="file"
                  accept="video/mp4,video/webm,video/ogg,video/quicktime,.mp4,.webm,.ogg,.mov,.m4v,.avi"
                  onChange={(e) => setVideoFile(e.target.files?.[0] ?? null)}
                />
                <p className="text-xs text-muted-foreground">
                  {videoFile
                    ? videoFile.name
                    : form.videoKey
                      ? "Current file stays unless you choose a new one. Max 200MB."
                      : "mp4, webm, ogg, mov, m4v, or avi. Max 200MB."}
                </p>
              </div>
            ) : (
              <Input
                value={form.videoUrl}
                onChange={(e) => setForm((prev) => ({ ...prev, videoUrl: e.target.value }))}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            )}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Thumbnail</label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setThumbnailFile(e.target.files?.[0] ?? null)}
            />
            {(thumbnailFile || existingThumbnailUrl) && (
              <img
                src={thumbnailPreview || existingThumbnailUrl}
                alt="Thumbnail preview"
                className="mt-2 h-16 w-28 rounded object-cover"
              />
            )}
          </div>
          <div className="flex items-center gap-2 pt-2">
            <input
              id="videoIsActive"
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            <label htmlFor="videoIsActive" className="text-sm font-medium">
              Active
            </label>
          </div>
        </div>
      </FormModal>

      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <CardTitle>Delete Video</CardTitle>
            </CardHeader>
            <CardContent className="flex h-full flex-col justify-between space-y-6 pb-6">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{videoToDelete?.title}</strong>? This action
                cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={closeDeleteConfirm} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button onClick={confirmDelete} disabled={isDeleting}>
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Card>
  );
}
